export interface DatadogConfig {
  appName: string;
}

let config: DatadogConfig | null = null;

/**
 * Configures the shared Datadog package with app-specific settings.
 *
 * In long-lived server processes (e.g., next dev), calling this once at startup
 * is sufficient. In serverless environments (e.g., Vercel), the in-memory config
 * won't persist across cold starts — set NEXT_PUBLIC_APP_NAME as an env var instead.
 *
 * @example
 * ```typescript
 * import { configureDatadog, initDatadogTracer } from "@marketing-sites/datadog";
 *
 * configureDatadog({ appName: "fin-ai" });
 * initDatadogTracer();
 * ```
 */
export const configureDatadog = (options: DatadogConfig) => {
  config = options;
};

/**
 * Returns the current config.
 *
 * Resolution order:
 * 1. Programmatic config via configureDatadog()
 * 2. NEXT_PUBLIC_APP_NAME environment variable
 * 3. Throws if neither is available
 */
export const getConfig = (): DatadogConfig => {
  if (config) {
    return config;
  }

  const appName = process.env.NEXT_PUBLIC_APP_NAME;
  if (appName) {
    return { appName };
  }

  throw new Error(
    "[@marketing-sites/datadog] No app name configured. Either call configureDatadog({ appName }) or set NEXT_PUBLIC_APP_NAME.",
  );
};
